"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useUnit } from "effector-react";
import { $localizeEnabledFlag } from "@features/amplitude-flags/model";
import {
    localizeConfig,
    localizeScriptUrl,
    thirdPartyDisabledExactPaths,
    thirdPartyDisabledPrefixes,
    thirdPartyOnboardingWhitelist
} from "@features/localize/config";

const LOCALIZE_SCRIPT_ID = "localize-script";
const LOCALIZE_SCRIPT_LOADED_ATTR = "data-localize-loaded";
const LOCALIZE_MAX_ATTEMPTS = 20;
const LOCALIZE_RETRY_DELAY = 200;
const FRESHWORKS_SCRIPT_ID = "freshworks-widget";
const FRESHWORKS_WIDGET_ID = 205000000295;
const FRESHWORKS_SCRIPT_URL = "https://euc-widget.freshworks.com/widgets/205000000295.js";
const HOTJAR_CONFIG_SCRIPT_ID = "hotjar-config";
const HOTJAR_LOADER_SCRIPT_ID = "hotjar-script";
const HOTJAR_ID = process.env.NEXT_PUBLIC_HOTJAR_ID ? Number(process.env.NEXT_PUBLIC_HOTJAR_ID) : null;
const HOTJAR_ENABLED_PREFIXES = ["/upsell"];

const getThirdPartyWindow = () => window as ThirdPartyWindow;

let localizeInitTimer: number | null = null;

export function ThirdPartyScriptsManager() {
    const pathname = usePathname() || "";
    const localizeEnabled = useUnit($localizeEnabledFlag);

    useEffect(() => {
        manageLocalize(pathname, localizeEnabled);
        manageFreshworks(pathname);
        manageHotjar(pathname);
    }, [pathname, localizeEnabled]);

    return null;
}

function manageLocalize(pathname: string, localizeEnabled: boolean | null) {
    if (typeof window === "undefined") return;
    if (localizeEnabled !== true) {
        teardownLocalize();
        return;
    }
    if (shouldDisableThirdParty(pathname)) {
        teardownLocalize();
        return;
    }
    ensureLocalize();
}

function manageFreshworks(pathname: string) {
    if (typeof window === "undefined") return;
    if (shouldDisableThirdParty(pathname)) {
        teardownFreshworks();
        return;
    }
    ensureFreshworksWidget();
}

function shouldDisableThirdParty(pathname: string) {
    if (isOnboardingWhitelisted(pathname)) {
        return false;
    }

    if (thirdPartyDisabledExactPaths.includes(pathname)) {
        return true;
    }

    for (const prefix of thirdPartyDisabledPrefixes) {
        if (pathname.startsWith(prefix)) {
            return true;
        }
    }

    return false;
}

function isOnboardingWhitelisted(pathname: string) {
    return thirdPartyOnboardingWhitelist.some((allowedPath) => pathname === allowedPath);
}

function ensureLocalize() {
    ensureLocalizeStub();
    ensureLocalizeScript();
    attemptLocalizeInit();
}

function ensureLocalizeStub() {
    if (typeof window === "undefined") return;
    const thirdPartyWindow = getThirdPartyWindow();
    if (thirdPartyWindow.Localize) return;
    const pending: Array<[string, unknown[]]> = [];
    const methods = [
        "translate",
        "untranslate",
        "phrase",
        "initialize",
        "translatePage",
        "setLanguage",
        "getLanguage",
        "getSourceLanguage",
        "detectLanguage",
        "getAvailableLanguages",
        "setWidgetLanguages",
        "hideLanguagesInWidget",
        "untranslatePage",
        "bootstrap",
        "prefetch",
        "on",
        "off",
        "hideWidget",
        "showWidget"
    ];
    const stub: LocalizeClient = { _e: pending };
    methods.forEach((method) => {
        stub[method] = (...args: unknown[]) => {
            pending.push([method, args]);
        };
    });
    thirdPartyWindow.Localize = stub as ThirdPartyWindow["Localize"];
}

function ensureLocalizeScript() {
    if (typeof document === "undefined") return;
    const existing = document.getElementById(LOCALIZE_SCRIPT_ID);
    if (existing) {
        const thirdPartyWindow = getThirdPartyWindow();
        thirdPartyWindow.__localizeScriptLoaded = existing.getAttribute(LOCALIZE_SCRIPT_LOADED_ATTR) === "true";
        return;
    }
    const script = document.createElement("script");
    script.id = LOCALIZE_SCRIPT_ID;
    script.src = localizeScriptUrl;
    script.async = true;
    script.addEventListener("load", () => {
        script.setAttribute(LOCALIZE_SCRIPT_LOADED_ATTR, "true");
        const thirdPartyWindow = getThirdPartyWindow();
        thirdPartyWindow.__localizeScriptLoaded = true;
        attemptLocalizeInit();
    });
    script.addEventListener("error", () => {
        script.removeAttribute(LOCALIZE_SCRIPT_LOADED_ATTR);
        const thirdPartyWindow = getThirdPartyWindow();
        thirdPartyWindow.__localizeScriptLoaded = false;
    });
    document.head.appendChild(script);
}

function attemptLocalizeInit() {
    if (typeof window === "undefined") return;
    const thirdPartyWindow = getThirdPartyWindow();
    if (!thirdPartyWindow.__localizeScriptLoaded) {
        scheduleLocalizeInitRetry();
        return;
    }
    if (initLocalize()) {
        return;
    }
    scheduleLocalizeInitRetry();
}

function initLocalize() {
    if (typeof window === "undefined") return false;
    const thirdPartyWindow = getThirdPartyWindow();
    const instance = thirdPartyWindow.Localize as LocalizeClient | undefined;
    if (!instance || typeof instance.initialize !== "function") {
        return false;
    }
    if (!thirdPartyWindow.__localizeInitialized) {
        instance.initialize(localizeConfig);
        flushLocalizeQueue(instance);
        thirdPartyWindow.__localizeInitialized = true;
    }
    translate(instance);
    return true;
}

function flushLocalizeQueue(instance: LocalizeClient) {
    const queue = Array.isArray(instance._e) ? instance._e.slice() : [];
    if (Array.isArray(instance._e)) {
        instance._e.length = 0;
    }
    queue.forEach((entry) => {
        const method = entry?.[0];
        const args = Array.isArray(entry?.[1]) ? entry[1] : [];
        if (!method || method === "initialize") return;
        const target = instance[method];
        if (typeof target === "function") {
            try {
                target.apply(instance, args);
            } catch (error) {
                console.error("Localize command failed", method, error);
            }
        }
    });
}

function translate(instance: LocalizeClient) {
    if (typeof instance.translatePage === "function") {
        instance.translatePage();
    }
}

function teardownLocalize() {
    if (typeof window === "undefined") return;
    const thirdPartyWindow = getThirdPartyWindow();
    const script = document.getElementById(LOCALIZE_SCRIPT_ID);
    if (script && script.parentNode) {
        script.parentNode.removeChild(script);
        script.removeAttribute(LOCALIZE_SCRIPT_LOADED_ATTR);
    }
    const existing = thirdPartyWindow.Localize as LocalizeClient | undefined;
    if (typeof existing?.untranslatePage === "function") {
        try {
            existing.untranslatePage();
        } catch {}
    }
    delete thirdPartyWindow.Localize;
    thirdPartyWindow.__localizeScriptLoaded = false;
    thirdPartyWindow.__localizeInitialized = false;
    clearLocalizeTimer();
}

function clearLocalizeTimer() {
    if (localizeInitTimer !== null) {
        window.clearInterval(localizeInitTimer);
        localizeInitTimer = null;
    }
}

function scheduleLocalizeInitRetry() {
    if (localizeInitTimer !== null) {
        return;
    }
    let attempts = 0;
    localizeInitTimer = window.setInterval(() => {
        const thirdPartyWindow = getThirdPartyWindow();
        if (!thirdPartyWindow.__localizeScriptLoaded) {
            return;
        }
        attempts += 1;
        if (initLocalize() || attempts >= LOCALIZE_MAX_ATTEMPTS) {
            clearLocalizeTimer();
        }
    }, LOCALIZE_RETRY_DELAY);
}

function ensureFreshworksWidget() {
    if (typeof window === "undefined") return;
    const thirdPartyWindow = getThirdPartyWindow();
    thirdPartyWindow.fwSettings = { widget_id: FRESHWORKS_WIDGET_ID };
    if (typeof thirdPartyWindow.FreshworksWidget !== "function") {
        const stub: FreshworksWidget = (...args: unknown[]) => {
            stub.q.push(args);
        };
        stub.q = [];
        thirdPartyWindow.FreshworksWidget = stub;
    }
    if (document.getElementById(FRESHWORKS_SCRIPT_ID)) {
        return;
    }
    const script = document.createElement("script");
    script.id = FRESHWORKS_SCRIPT_ID;
    script.src = FRESHWORKS_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
}

function teardownFreshworks() {
    if (typeof window === "undefined") return;
    const script = document.getElementById(FRESHWORKS_SCRIPT_ID);
    if (script && script.parentNode) {
        script.parentNode.removeChild(script);
    }
    const thirdPartyWindow = getThirdPartyWindow();
    const instance = thirdPartyWindow.FreshworksWidget;
    if (typeof instance === "function") {
        try {
            instance("hide", "launcher");
        } catch {}
    }
    delete thirdPartyWindow.FreshworksWidget;
    delete thirdPartyWindow.fwSettings;
}

function manageHotjar(pathname: string) {
    if (typeof window === "undefined" || !HOTJAR_ID) return;
    const shouldEnable = HOTJAR_ENABLED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
    if (shouldEnable) {
        ensureHotjar();
    } else {
        teardownHotjar();
    }
}

function ensureHotjar() {
    if (typeof document === "undefined" || !HOTJAR_ID) return;
    if (document.getElementById(HOTJAR_LOADER_SCRIPT_ID)) return;

    const configScript = document.createElement("script");
    configScript.id = HOTJAR_CONFIG_SCRIPT_ID;
    configScript.textContent = `window.hj=window.hj||function(){(window.hj.q=window.hj.q||[]).push(arguments)};window._hjSettings={hjid:${HOTJAR_ID},hjsv:6};`;
    document.head.appendChild(configScript);

    const loaderScript = document.createElement("script");
    loaderScript.id = HOTJAR_LOADER_SCRIPT_ID;
    loaderScript.src = `https://static.hotjar.com/c/hotjar-${HOTJAR_ID}.js?sv=6`;
    loaderScript.async = true;
    document.head.appendChild(loaderScript);
}

function teardownHotjar() {
    if (typeof document === "undefined") return;
    [HOTJAR_CONFIG_SCRIPT_ID, HOTJAR_LOADER_SCRIPT_ID].forEach((id) => {
        const el = document.getElementById(id);
        if (el?.parentNode) el.parentNode.removeChild(el);
    });
    const w = getThirdPartyWindow();
    // Once the Hotjar script has run, its code stays: it has wrapped CSSStyleSheet.insertRule and
    // other platform methods with functions that read `hj.tryCatch` and `_hjSettings`. Replacing
    // `hj` with a stub and deleting the settings made every such call throw ("hj.tryCatch is not
    // a function"), which crashed any component inserting a style rule after the upsell pages.
    // So the globals stay as they are; removing the tags only keeps Hotjar from being added again.
    if (typeof (w.hj as any)?.tryCatch === "function") return;
    w.hj = function () {};
    (w.hj as any).q = [];
    delete w._hjSettings;
}

type LocalizeClient = {
    _e?: Array<[string, unknown[]]>;
    initialize?: (config: typeof localizeConfig) => void;
    translatePage?: () => void;
    untranslatePage?: () => void;
    [key: string]: any;
};

type FreshworksWidget = ((...args: unknown[]) => void) & { q: unknown[] };

type ThirdPartyWindow = Window & {
    Localize?: LocalizeClient;
    __localizeInitialized?: boolean;
    __localizeScriptLoaded?: boolean;
    fwSettings?: { widget_id: number };
    FreshworksWidget?: FreshworksWidget;
    hj?: ((...args: unknown[]) => void) & { q?: unknown[] };
    _hjSettings?: { hjid: number; hjsv: number };
};
